import { callAPI, debouncedCallAPI } from "@/app/hooks/useApi";
import { useCallback, useEffect, useRef, useState } from "react";
import toasty from "@/app/util/toasty";
import log from "@/app/util/log";

const usePreferences = (userId, token) => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [preferences, setPreferences] = useState(null);
	const [loadedUserId, setLoadedUserId] = useState(null);
	const shouldFetch = Boolean(userId && token);
	const createPreferences = async () => {
		if (!userId) return;
		try {
			await callAPI(
				"users_data",
				{
					method: "POST",
					payload: {
						id: userId,
						preferences: {},
					},
					schema: "portal",
				},
				token
			);
			fetch();
		} catch (e) {
			setError(e);
			setLoadedUserId(userId);
		}
	};
	const fetch = useCallback(async () => {
		if (!userId) return;
		setIsLoading(true);
		try {
			const data = await callAPI(
				"users_data",
				{
					method: "GET",
					query: {
						id: `eq.${userId}`,
					},
					schema: "portal",
					one: true,
					logError: false,
				},
				token
			);
			setPreferences(data?.preferences || {});
			setLoadedUserId(userId);
		} catch (e) {
			if (e.message.includes("Requested object does not exist")) {
				await createPreferences();
				setIsLoading(false);
				return;
			} else {
				log.error(e, { userId, url: "users_data", method: "GET" });
			}
			setError(e);
			setLoadedUserId(userId);
		} finally {
			setIsLoading(false);
		}
	}, [userId, token]);
	const mutate = (newPreferences, shouldRevalidate = true) => {
		setPreferences(newPreferences);
		if (shouldRevalidate) {
			fetch();
		}
	};

	useEffect(() => {
		setPreferences(shouldFetch ? null : {});
		setLoadedUserId(shouldFetch ? null : userId);
		setError(null);
		setIsLoading(false);
	}, [shouldFetch, userId, token]);

	useEffect(() => {
		if (!userId) return;
		fetch();
	}, [userId, fetch]);

	return {
		preferences: preferences || {},
		isLoading:
			(isLoading || (shouldFetch && loadedUserId !== userId)) && !error,
		error,
		mutate,
	};
};

const useUserPreferences = (userId, token) => {
	const queueRef = useRef({});
	const { preferences, isLoading, error, mutate } = usePreferences(
		userId,
		token
	);
	const checkPreference = (feature: string, value = true) => {
		return preferences?.[feature] === value;
	};
	const checkPreferenceExpired = (
		feature: string,
		defaultValue: number = 0
	) => {
		const prefDate = new Date(preferences?.[feature] || defaultValue);
		if (!(prefDate instanceof Date) || isNaN(prefDate.getTime()))
			return false;
		const now = new Date();
		return now.getTime() > prefDate.getTime();
	};

	const setPreference = (
		feature: string,
		value: string | boolean | number
	) => {
		if (!userId) {
			return;
		}
		const payload = {
			...preferences,
			...queueRef.current,
			[feature]: value,
		};
		debouncedCallAPI(
			"users_data",
			{
				method: "PATCH",
				query: {
					id: `eq.${userId}`,
				},
				payload: { preferences: payload },
				schema: "portal",
			},
			() => {
				// clear the queue on success
				queueRef.current = {};
				mutate(payload, false);
			},
			(error) => {
				queueRef.current = {};
				toasty.error("Failed to update preferences", error);
			},
			token
		);
		// mutate immediately to carry over preference changes to support debounced payloads
		queueRef.current = {
			...queueRef.current,
			[feature]: value,
		};
	};

	const setPreferenceWithExpiry = (feature: string, value: number) => {
		const expiryDate = new Date(Date.now() + value);
		setPreference(feature, expiryDate.toISOString());
	};

	return {
		preferences,
		error,
		isLoading,
		checkPreference,
		setPreference,
		setPreferenceWithExpiry,
		checkPreferenceExpired,
	};
};

export default useUserPreferences;
